<template>
  <div class="p-4 bg-gray-50 dark:bg-gray-900 min-h-screen">
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Gestión de Sedes</h1>
        <p class="text-sm text-gray-600 dark:text-gray-400">Administra las sucursales o franquicias</p>
      </div>
      <div>
        <button
          @click="openCreateModal"
          class="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow hover:shadow-md"
        >
          <Icon icon="mdi:plus" class="mr-2" />
          Nueva Sede
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-4 border border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-4">
        <div class="flex-1">
          <label class="block text-xs font-bold text-gray-400 uppercase mb-1">Buscar Sede</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input v-model="searchQuery" type="text" placeholder="Nombre de la sede..." class="w-full pl-10 pr-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" />
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Sede</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Dirección</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-if="loading">
              <td colspan="4" class="px-6 py-12 text-center text-gray-500">
                <Icon icon="mdi:loading" class="animate-spin text-3xl mx-auto mb-2" />
                Cargando sedes...
              </td>
            </tr>
            <tr v-else-if="filteredSedes.length === 0">
              <td colspan="4" class="px-6 py-12 text-center text-gray-500">
                No se encontraron sedes.
              </td>
            </tr>
            <tr v-else v-for="sede in filteredSedes" :key="sede.idSede" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex flex-col justify-center items-center mr-3">
                    <Icon icon="mdi:store-marker" class="text-blue-600 dark:text-blue-300 text-xl" />
                  </div>
                  <div>
                    <div class="text-sm font-bold text-gray-900 dark:text-white">{{ sede.nombre }}</div>
                    <div class="text-xs text-gray-500">ID: {{ sede.idSede }}</div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                {{ sede.direccion || 'N/A' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="[
                    'px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
                    sede.estado?.toLowerCase() === 'activo' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  ]">
                  {{ sede.estado }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-2">
                <button @click="openEditModal(sede)" class="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 transition-colors" title="Editar">
                  <Icon icon="mdi:pencil" />
                </button>
                <button @click="toggleStatus(sede)" class="p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-100 transition-colors" title="Cambiar Estado">
                  <Icon :icon="sede.estado?.toLowerCase() === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal Formulario -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-hidden flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div class="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg shadow-2xl animate-fadeIn">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ editingItem ? 'Editar Sede' : 'Nueva Sede' }}</h3>
          <button @click="closeModal" class="text-gray-500 hover:text-gray-700"><Icon icon="mdi:close" class="text-xl" /></button>
        </div>
        <form @submit.prevent="saveForm" class="p-6">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre</label>
            <input v-model="form.nombre" type="text" required class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" @blur="form.nombre = toTitleCase(form.nombre)">
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dirección</label>
            <textarea v-model="form.direccion" rows="2" class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" @blur="form.direccion = toTitleCase(form.direccion)"></textarea>
          </div>
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estado</label>
            <select v-model="form.estado" class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </div>
          <div class="flex justify-end gap-3">
            <button type="button" @click="closeModal" class="px-4 py-2 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">Cancelar</button>
            <button type="submit" :disabled="saving" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:bg-blue-400">
              {{ saving ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { toTitleCase } from "@/utils/stringUtils.js";
import { obtenerSedes, crearSede, actualizarSede, cambiarEstadoSede } from "@/api/sede.js";
import { getCurrentUser } from "@/utils/permissionUtils";

export default {
  name: 'SedesList',
  components: { Icon },
  data() {
    return {
      sedes: [],
      loading: false,
      saving: false,
      showModal: false,
      searchQuery: '',
      editingItem: null,
      form: { idSede: null, nombre: '', direccion: '', estado: 'activo' }
    };
  },
  computed: {
    filteredSedes() {
      if (!this.searchQuery) return this.sedes;
      return this.sedes.filter(s => s.nombre.toLowerCase().includes(this.searchQuery.toLowerCase()));
    }
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    toTitleCase,
    async fetchData() {
      this.loading = true;
      try {
        const res = await obtenerSedes();
        if (res.success) this.sedes = res.data;
        else this.toast.error("Error al obtener sedes");
      } catch (err) {
        this.toast.error("Error crítico de red");
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.editingItem = null;
      this.form = { idSede: null, nombre: '', direccion: '', estado: 'activo' };
      this.showModal = true;
    },
    openEditModal(sede) {
      this.editingItem = sede;
      this.form = { ...sede };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async saveForm() {
      this.saving = true;
      try {
        let res;
        if (this.editingItem) {
          res = await actualizarSede(this.form.idSede, this.form);
        } else {
          res = await crearSede(this.form);
        }

        if (res.success) {
          this.toast.success(res.message);
          this.closeModal();
          this.fetchData();
        } else {
          this.toast.error(res.error || "Hubo un problema al guardar");
        }
      } catch (e) {
        this.toast.error("Error excepcional");
      } finally {
        this.saving = false;
      }
    },
    async toggleStatus(sede) {
      const nuevoEstado = sede.estado.toLowerCase() === 'activo' ? 'inactivo' : 'activo';
      if (!confirm(`¿Estás seguro de cambiar el estado de la sede a ${nuevoEstado}?`)) return;
      
      const currentUser = getCurrentUser();
      const userId = currentUser?.idUsuario || currentUser?.id || 1;
      
      try {
        const res = await cambiarEstadoSede(sede.idSede, nuevoEstado, userId);
        if (res.success) {
          this.toast.success("Estado actualizado");
          this.fetchData();
        } else {
          this.toast.error("No se pudo cambiar el estado");
        }
      } catch (e) {
        this.toast.error("Error al cambiar de estado");
      }
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }
</style>
